/* ============================================================
   phoneRunDump.js — larian PUKAL phone untuk app enjin 'dump' (TikTok, 4 Nov 2026)
   ============================================================
   Dipanggil dari handleRunPhone (WorkspaceView) bila app terpilih `engine:'dump'`.
   Enjin Shopee (video + tag produk + kalibrasi) TIDAK disentuh — ia kekal di
   handleRunPhone.

   Keputusan user 4 Nov (PLAN_PHONE_TIKTOK_FB.md):
   - Semua = DRAFT (mode sentiasa 'draft' untuk app draftOnly).
   - Kandungan dari CARD (blank card atau card module):
       video  → set PERTAMA yang ada video (set1 → set2 → set3)
       image  → gambar utama card + gambar tambahan, IKUT SUSUNAN card (= susunan slide)
       text   → caption card (kotak Facebook Caption → TikTok, captionUntukPhone)
   - Caption = captionUntukPhone (sama Shopee phone + robot FB).
   - BULK-FIRST: semua card ditick dijalankan satu demi satu, satu klik.
   Draft TikTok tidak terbit (tak boleh tersalah post) → tiada pintu elak berganda.
   ============================================================ */

import { AndroidController, phoneVideoDest } from './androidAdb';
import { runRecipe } from './androidRunner';
import { recipeFor } from './androidRecipe';
import { captionUntukPhone, videoCardPalingBaru } from './androidPostCheck';
import { bacaPhoneContent, bacaPhoneAiLabel, bacaPhoneFbPage, bacaPhoneFbLink, bacaPhoneTtBag, PHONE_CONTENT_LABEL } from './phoneApps';
import { shell, delay } from './androidDumpKit';
import { muatAjaran } from './androidAjar';

const SET_ORDER = ['set1', 'set2', 'set3'];
const HAD_GAMBAR = 35; // had TikTok "You can select up to 35 photos" (diukur 4 Nov)

// imageBase64 boleh jadi data URL, URL biasa, atau base64 mentah.
function sumberGambar(s) {
  if (typeof s !== 'string' || !s.trim()) return '';
  const v = s.trim();
  if (/^(https?:|data:image\/|blob:)/i.test(v)) return v;
  if (v.length > 200 && /^[A-Za-z0-9+/=\s]+$/.test(v)) return 'data:image/jpeg;base64,' + v.replace(/\s+/g, '');
  return '';
}

export function gambarCard(p) {
  const tambahan = Array.isArray(p?.extraImageUrls) ? p.extraImageUrls : [];
  const semua = [sumberGambar(p?.imageBase64) || sumberGambar(p?.imageUrl), ...tambahan.map(sumberGambar)].filter(Boolean);
  return [...new Set(semua)].slice(0, HAD_GAMBAR);
}

function setDenganCaption(p) {
  const pr = p?.prompts || {};
  return SET_ORDER.find((sk) => captionUntukPhone(pr[sk])) || 'set1';
}

/* Kumpul kerja ikut jenis kandungan. Pulang { kerja:[...], langkau:[sebab] }. */
export function kumpulKerja(toRun, content) {
  const kerja = []; const langkau = [];
  toRun.forEach((p) => {
    const nama = p.name || p.id;
    const pr = p.prompts || {};
    if (content === 'video') {
      const sk = SET_ORDER.find((k) => typeof pr[k]?.uploadedVideo === 'string' && /^https?:\/\//.test(pr[k].uploadedVideo));
      if (!sk) { langkau.push(`${nama}: tiada video`); return; }
      kerja.push({ p, nama, sk, url: pr[sk].uploadedVideo, caption: captionUntukPhone(pr[sk]) });
    } else if (content === 'image') {
      const imgs = gambarCard(p);
      if (!imgs.length) { langkau.push(`${nama}: tiada gambar`); return; }
      const sk = setDenganCaption(p);
      kerja.push({ p, nama, sk, imgs, caption: captionUntukPhone(pr[sk]) });
    } else {
      const sk = setDenganCaption(p);
      const text = captionUntukPhone(pr[sk]);
      if (!text) { langkau.push(`${nama}: tiada caption untuk post text`); return; }
      kerja.push({ p, nama, sk, text, caption: text });
    }
  });
  return { kerja, langkau };
}

const sambungan = (type) => (/png/i.test(type) ? 'png' : /webp/i.test(type) ? 'webp' : 'jpg');

async function muatBlob(url, jenis) {
  let res;
  try { res = await fetch(url); } catch { throw new Error(`${jenis} tak dapat dimuat turun (laman sumber menyekat). Simpan ${jenis} itu ke card semula.`); }
  if (!res.ok) throw new Error(`${jenis} tak dapat dimuat turun (${res.status}).`);
  const blob = await res.blob();
  if (!blob || !blob.size) throw new Error(`${jenis} kosong.`);
  return blob;
}

/* deps = { toRun, app, ctlRef, stopRef, setAutomationProgress, tulisStatusAkaunKad,
            cerminStatusKeSlot, akaunId } */
export async function jalanPhoneDump(deps) {
  const { toRun, app, ctlRef, stopRef, setAutomationProgress, tulisStatusAkaunKad, cerminStatusKeSlot, akaunId = '' } = deps;
  const content = bacaPhoneContent(app);
  const aiLabel = app.aiLabel ? bacaPhoneAiLabel() : undefined;
  const jenisLabel = PHONE_CONTENT_LABEL[content] || content;
  // FB: page (kosong = profile) + link Shopee pada reel (link card: shopeeLink → link).
  const fbPage = app.fbOptions ? bacaPhoneFbPage() : '';
  const fbLink = !!(app.fbOptions && content === 'video' && bacaPhoneFbLink());
  // TikTok beg kuning (video): produk dicari ikut NAMA card (= tajuk produk TikTok) dan disahkan
  // pada skrin TikTok. Card tanpa link produk TikTok (`link`, takrifan linkTiktokUntukRobot) →
  // dijalankan TANPA beg + makluman (peraturan user: beg gagal → draf).
  const ttBag = !!(app.bag && content === 'video' && bacaPhoneTtBag());
  // CTA pelanggan (Control Panel / PRO Studio) — teks beg kuning TikTok + "Link name" FB.
  const ctaText = String(deps.ctaText || '').trim();
  const { kerja, langkau } = kumpulKerja(toRun, content);
  if (fbLink) {
    for (let i = kerja.length - 1; i >= 0; i--) {
      const l = String(kerja[i].p.shopeeLink || kerja[i].p.link || '').trim();
      if (/^https?:\/\/\S*(shopee|shp\.ee)/i.test(l)) kerja[i].productLink = l;
      else { langkau.push(`${kerja[i].nama}: tiada link Shopee pada card`); kerja.splice(i, 1); }
    }
    if (!kerja.length) { alert('Tiada card yang ada link Shopee:\n' + langkau.slice(0, 8).join('\n')); return; }
  }
  if (!kerja.length) {
    alert(langkau.length
      ? `Tiada card yang boleh dijalankan (${jenisLabel}):\n` + langkau.slice(0, 8).join('\n')
      : 'Tick card dahulu.');
    return;
  }
  if (langkau.length && !window.confirm(`${langkau.length} card akan dilangkau:\n${langkau.slice(0, 8).join('\n')}\n\nTeruskan card yang lain?`)) return;

  const tutupProgress = () => setAutomationProgress((prev) => ({ ...prev, isActive: false }));
  stopRef.current = false;
  setAutomationProgress({
    isActive: true, type: 'phone', status: 'running', currentStep: 0, totalSteps: 1,
    steps: [{ label: 'Connecting to your phone', status: 'running' }],
    message: `Saving ${kerja.length} ${kerja.length === 1 ? 'draft' : 'drafts'} (${jenisLabel.toLowerCase()}) to ${app.label}`, details: 'Starting the robot…', extensionStatus: 'unknown',
  });
  if (!ctlRef.current) ctlRef.current = new AndroidController(() => {}, () => {});
  const ctl = ctlRef.current;
  try {
    await ctl.reconnect();
    await ctl.getDeviceInfo();
  } catch (e) {
    tutupProgress();
    alert(e?.code === 'NO_GRANTED'
      ? 'Telefon belum disambung pada browser ini. Buka "Set up phone" dahulu untuk sambung telefon, kemudian Run semula.'
      : 'Tak dapat sambung telefon: ' + (e?.message || e));
    return;
  }
  const serial = ctl.info?.serial || '';

  // Status card: pf 'sp' = larian telefon (rekodLarianKad). TIADA `sp` (catatan Shopee) —
  // larian ini bukan Shopee, jadi tidak menyekat Shopee.
  const rekod = (k, st, sb = '') => ({ pid: k.p.id, s: k.sk, akaunId, st, sb, md: '', pf: 'sp' });
  const catat = (senarai) => { tulisStatusAkaunKad(senarai); if (akaunId) cerminStatusKeSlot(senarai); };
  catat(kerja.map((k) => rekod(k, 'running')));

  const recipe = recipeFor(app.id);
  // Ajaran pelanggan (Ajar robot, disimpan dalam akaun) dipakai dalam larian pukal juga.
  // Larian pukal TIDAK berhenti meminta ajaran (tiada mintaAjar) — itu di Set up & test Step 3.
  const ajaran = await muatAjaran(app.id);
  const labelStep = (recipe.stepsFor ? recipe.stepsFor(content, { page: fbPage, link: fbLink, bag: ttBag }) : recipe.steps).map((s) => s.labelEn);
  const statusStep = { running: 'running', pass: 'done', healed: 'done', error: 'error' };
  let draf = 0, dihentikan = 0; const gagal = []; const takLengkap = []; const notaSet = new Set();
  for (let i = 0; i < kerja.length; i++) {
    if (stopRef.current) {
      dihentikan = kerja.length - i;
      catat(kerja.slice(i).map((k) => rekod(k, '')));
      break;
    }
    const k = kerja[i];
    const steps = [{ label: content === 'text' ? 'Prepare text' : `Send ${jenisLabel.toLowerCase()} to phone`, status: 'running' }, ...labelStep.map((label) => ({ label, status: 'pending' }))];
    const papar = () => setAutomationProgress((prev) => ({
      ...prev, steps: steps.map((s) => ({ ...s })), totalSteps: steps.length,
      details: `Card ${i + 1} of ${kerja.length}: ${k.nama}`,
    }));
    papar();
    try {
      const params = { content, caption: k.caption, text: k.text, aiLabel, fbPage, productLink: k.productLink || '', ctaText, linkName: ctaText };
      if (ttBag) {
        if (String(k.p.link || '').trim()) { params.begKuning = true; params.productName = k.p.name || ''; }
        else notaSet.add('Card tanpa link produk TikTok disimpan sebagai Draft TANPA beg kuning.');
      }
      if (content === 'video') {
        const blob = await muatBlob(k.url, 'Video');
        const dest = phoneVideoDest(k.nama, k.p.id, k.sk);
        await ctl.pushVideo(blob, dest, () => {});
        await ctl.mediaScan(dest);
        if (!(await videoCardPalingBaru(ctl, dest))) throw new Error('Video card bukan video terbaru dalam galeri phone. Run semula.');
        params.videoDest = dest;
      } else if (content === 'image') {
        await shell(ctl, ['mkdir', '-p', '/sdcard/DCIM/SESAAT']);
        const id8 = String(k.p.id || '').replace(/[^a-zA-Z0-9]/g, '').slice(-8);
        const cop = Date.now().toString(36);
        params.imageNames = [];
        for (let g = 0; g < k.imgs.length; g++) {
          const blob = await muatBlob(k.imgs[g], `Gambar ${g + 1}`);
          const nama = `SESAAT_${id8}_${k.sk}_${cop}_${g + 1}.${sambungan(blob.type)}`;
          const dest = `/sdcard/DCIM/SESAAT/${nama}`;
          await ctl.pushVideo(blob, dest, () => {});
          await ctl.mediaScan(dest);
          params.imageNames.push(nama);
          // Jarak > 1 saat: galeri menyusun ikut saat ditambah — dua gambar dalam saat
          // yang sama boleh tersusun terbalik (diukur 4 Nov).
          await delay(1300);
        }
        await delay(1500);
      }
      steps[0].status = 'done';
      papar();
      const r = await runRecipe(ctl, { appId: app.id, serial, mode: 'draft', params, ajar: { ajaran } }, {
        onLog: () => {},
        onStep: (st) => {
          if (!steps[st.index + 1]) return;
          steps[st.index + 1].status = statusStep[st.status] || 'pending';
          papar();
        },
      });
      (r.nota || []).forEach((n) => notaSet.add(n));
      if (r.status === 'draft_saved') { draf++; catat([rekod(k, 'draft')]); }
      else if (r.draftSaved) {
        const kurang = (r.belumSiap || []).join(', ') || 'isi';
        takLengkap.push(`${k.nama}: belum disahkan ${kurang} (${r.reason || 'gagal'})`);
        catat([rekod(k, 'draft', `Tidak lengkap: ${kurang}`)]);
      } else if (r.failedAt && r.failedAt !== 'finish' && r.status === 'failed' && !r.draftSaved && /tak dijumpai|tak terbuka/.test(r.reason || '')) {
        // Butang tak dijumpai = phone/akaun ini berbeza rupa → arahkan ke Ajar robot.
        gagal.push(`${k.nama}: ${r.reason} — buka Set up & test → Step 3: Configure, tekan Run test dan tunjuk butang yang robot minta.`);
        catat([rekod(k, 'failed', r.reason || 'gagal')]);
      } else if (r.failedAt === 'finish') {
        gagal.push(`${k.nama}: Draft tak dapat disahkan — semak Drafts di phone sebelum Run semula (${r.reason})`);
        catat([rekod(k, 'uncertain', r.reason || '')]);
      } else {
        gagal.push(`${k.nama}: ${r.reason || 'gagal'}`);
        catat([rekod(k, 'failed', r.reason || 'gagal')]);
      }
    } catch (err) {
      gagal.push(`${k.nama}: ${err?.message || err}`);
      catat([rekod(k, 'failed', String(err?.message || err).slice(0, 300))]);
    }
  }
  const baris = [];
  if (draf) baris.push(app.id === 'facebook'
    ? `📝 ${draf} disimpan sebagai Draft di Facebook${fbPage ? ` (page ${fbPage})` : ''} — terbit sendiri dari Drafts di app Facebook.`
    : `📝 ${draf} disimpan sebagai Draft di ${app.label} (buka ${app.label} → Profile → Drafts untuk terbit sendiri).`);
  notaSet.forEach((n) => baris.push(`ℹ️ ${n}`));
  if (langkau.length) baris.push(`⚠️ ${langkau.length} dilangkau: ${langkau.slice(0, 3).join(' · ')}${langkau.length > 3 ? ' …' : ''}`);
  if (dihentikan) baris.push(`⏹️ Dihentikan: ${dihentikan} belum dijalankan.`);
  if (takLengkap.length) baris.push(`⚠️ ${takLengkap.length} Draft TIDAK lengkap. Lengkapkan dahulu sebelum terbit:\n` + takLengkap.join('\n'));
  if (gagal.length) baris.push(`⚠️ ${gagal.length} gagal: ` + gagal.slice(0, 5).join(' · ') + (gagal.length > 5 ? ' …' : ''));
  if (!baris.length) baris.push('Tiada apa diproses.');
  setAutomationProgress((prev) => ({
    ...prev, steps: [], totalSteps: 0, details: baris.join('\n'), message: 'Phone drafts finished',
    status: gagal.length || takLengkap.length ? 'error' : 'success',
  }));
}
